import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./CreateOffering.css";
import { Spinner } from "../../../components/Filtering/Reusable/Spinner";
import { useTranslation } from "react-i18next";

import BreadCrumb from "../../../components/HeaderComponents/BreadCrum";
import ArrowIcon from "../../../utils/ArrowIcon";

import DropDownTheme from "../../../components/buttons/DropDownTheme";
import DropDownFrecuency from "../../../components/buttons/DropDownFrecuency";
import DropDownLanguage from "../../../components/buttons/DropDownLanguage";
import DropDownCreator from "../../../components/buttons/DropdownCreator";

import MultiLang from "../../../components/MultiLang/MultiLang";
import {
  toggleSection,
  validateURL,
  validateVersion,
  getControlledList,
} from "../../../utils/constants";

import {
  setTextForField,
  getTextForField,
  removeLanguageTab,
  addLanguageTab,
} from "../../../utils/formUtils";

import { useWorkerApiHelper } from "../../../hooks/useWorkerApiHelper";
import { availableLanguages } from "../../../i18n";
import { usePostDataset } from "../../../hooks/datasets/usePostDataset";
import Distribution from "../../OfferingData/Distribution/Distribution";
import DropDownRights from "../../../components/buttons/DropDownRights";
import { useWizardCreationDataset } from "../../../hooks/datasets/useWizardCreationDataset";
import KeywordsInput from "../../../components/Keywords/KeywordsInput";
import DropDownCountry from "../../../components/buttons/DropDownCountry";
import { useGetAgents } from "../../../hooks/agents/useGetAgents";
import { useAuthRoles } from "../../../hooks/useAuthRoles";
import { useGetParticipantByParticipantId } from "../../../hooks/participants/useGetParticipantByParticipantId";
import {
  buildCreatorOptions,
  getParticipantIdFromToken,
  parseList,
} from "./offeringFormUtils";

function CreateOffering() {
  const { callApiWithWorker } = useWorkerApiHelper();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { decoded } = useAuthRoles();
  const [baseLang] = useState(() => i18n.language);
  const creatorTouchedRef = useRef(false);

  const [isGeneralOpen, setIsGeneralOpen] = useState(true);
  const [isAdditionalOpen, setIsAdditionalOpen] = useState(true);
  const [isDistributionOpen, setIsDistributionOpen] = useState(true);

  const [landingPageError, setLandingPageError] = useState("");
  const { selectedPolicies, selectedPolicyIris } = useWizardCreationDataset();
  const { data: agentsData } = useGetAgents({
    callApiWithWorker,
    enabled: true,
  });
  const participantIdFromToken = getParticipantIdFromToken(decoded);
  const { data: participantData } = useGetParticipantByParticipantId({
    callApiWithWorker,
    participantId: participantIdFromToken,
    enabled: !!participantIdFromToken,
  });
  const [isFormValid, setIsFormValid] = useState(false);

  const [controlledVocabularies, setControlledVocabularies] = useState({
    frequencies: [],
    languages: [],
    themes: [],
    rights: [],
    countries: [],
  });

  const [languageTabs, setLanguageTabs] = useState([]);
  const [activeLang, setActiveLang] = useState("");
  const [versionError, setVersionError] = useState("");

  const [appLegislationText, setAppLegislationText] = useState("");
  const [legislationError, setLegislationError] = useState("");

  const [formData, setFormData] = useState({
    title: [{ language: baseLang, value: "" }],
    identifier: "",
    description: [{ language: baseLang, value: "" }],
    theme: [],
    modified: new Date().toISOString().split("T")[0],
    access_rights: "",
    creator: "",
    language: [],
    version_info: "",
    frequency: "",
    applicable_legislation: "",
    spatial: [],
    landing_page: "",
    version_notes: [{ language: baseLang, value: "" }],
    publisher: "",
    distributions: [],
    keywords: [],
  });

  const translatableFields = ["title", "description", "version_notes"];
  const isTranslationMode = activeLang !== "";
  const inputClass = isTranslationMode
    ? "inputDatasetdisabled"
    : "inputDataset";

  useEffect(() => {
    async function fetchVocabularies() {
      const [frequencies, languages, themes, rights, countries] =
        await Promise.all([
          getControlledList(callApiWithWorker, "frequencies", {
            lang: i18n.language,
          }),
          getControlledList(callApiWithWorker, "languages", {
            lang: i18n.language,
          }),
          getControlledList(callApiWithWorker, "data-theme", {
            lang: i18n.language,
          }),
          getControlledList(callApiWithWorker, "access-right-eu", {
            lang: i18n.language,
          }),
          getControlledList(callApiWithWorker, "countries", {
            lang: i18n.language,
          }),
        ]);

      setControlledVocabularies({
        frequencies,
        languages,
        themes,
        rights,
        countries,
      });
    }

    fetchVocabularies();
  }, []);

  const languageOptions = availableLanguages;

  const getLangLabel = (code) =>
    languageOptions.find((o) => o.code === code)?.label || code;

  const handleAddLanguageTab = (code) => {
    addLanguageTab(
      code,
      baseLang,
      languageTabs,
      translatableFields,
      formData,
      setFormData,
      setLanguageTabs,
      setActiveLang,
    );
  };

  const handleRemoveLang = (langCode) => {
    removeLanguageTab(
      langCode,
      activeLang,
      translatableFields,
      setFormData,
      setLanguageTabs,
      setActiveLang,
    );
  };

  const renderTranslatableInput = (fieldName, placeholder, isTextarea) => {
    const value = getTextForField(formData, fieldName, activeLang, baseLang);
    const lang = activeLang === "" ? baseLang : activeLang;

    if (isTextarea) {
      return (
        <textarea
          rows="8"
          className="inputDataset inputWide fontNoto"
          name={fieldName}
          placeholder={placeholder}
          value={value}
          onChange={(e) =>
            setFormData((prev) =>
              setTextForField(prev, fieldName, lang, e.target.value),
            )
          }
        />
      );
    }

    return (
      <input
        className="inputDataset inputWide"
        type="text"
        name={fieldName}
        placeholder={placeholder}
        value={value}
        onChange={(e) =>
          setFormData((prev) =>
            setTextForField(prev, fieldName, lang, e.target.value),
          )
        }
      />
    );
  };

  const handleSimpleChange = (e) => {
    if (activeLang !== "") return;

    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "landing_page") {
      const { error } = validateURL(value, t);
      setLandingPageError(error);
    }

    if (name === "version_info") {
      const { error } = validateVersion(value, t);
      setVersionError(error);
    }
  };

  const validateLegislationText = (value) => {
    const entries = parseList(value);
    if (entries.length === 0) return "";
    for (const entry of entries) {
      const { valid, error } = validateURL(entry, t);
      if (!valid) return error || t("common.invalid_url");
    }
    return "";
  };

  useEffect(() => {
    const baseTitle =
      formData.title?.find((x) => x.language === baseLang)?.value?.trim() ||
      formData.title?.[0]?.value?.trim() ||
      "";
    const baseDescription =
      formData.description
        ?.find((x) => x.language === baseLang)
        ?.value?.trim() ||
      formData.description?.[0]?.value?.trim() ||
      "";
    const baseVersionNotes =
      formData.version_notes
        ?.find((x) => x.language === baseLang)
        ?.value?.trim() ||
      formData.version_notes?.[0]?.value?.trim() ||
      "";
    const hasLanguage =
      Array.isArray(formData.language) &&
      (formData.language[0] || "").trim() !== "";
    const hasIdentifier = String(formData.identifier || "").trim() !== "";
    const hasTheme =
      Array.isArray(formData.theme) &&
      formData.theme.some((item) => String(item || "").trim() !== "");
    const hasAccessRights = String(formData.access_rights || "").trim() !== "";
    const hasModified = String(formData.modified || "").trim() !== "";
    const hasPublisher = String(formData.publisher || "").trim() !== "";
    const hasDistribution =
      Array.isArray(formData.distributions) &&
      formData.distributions.some((id) => String(id || "").trim() !== "");

    const valid =
      hasIdentifier &&
      baseTitle !== "" &&
      baseDescription !== "" &&
      hasLanguage &&
      hasTheme &&
      hasAccessRights &&
      hasModified &&
      hasPublisher &&
      hasDistribution &&
      landingPageError === "" &&
      baseVersionNotes !== "" &&
      versionError === "" &&
      legislationError === "";

    setIsFormValid(valid);
  }, [formData, landingPageError, versionError, legislationError, baseLang]);

  const { mutate: createOffering, isPending: loading } = usePostDataset({
    callApiWithWorker,
    navigate,
    t,
  });

  const handleDistributionSelectionChange = useCallback((ids) => {
    setFormData((prev) => {
      const currentIds = Array.isArray(prev.distributions)
        ? prev.distributions
        : [];
      const nextIds = Array.isArray(ids) ? ids : [];

      if (
        currentIds.length === nextIds.length &&
        currentIds.every((value, index) => value === nextIds[index])
      ) {
        return prev;
      }

      return {
        ...prev,
        distributions: nextIds,
      };
    });
  }, []);

  const creatorOptions = useMemo(
    () =>
      buildCreatorOptions({
        participant: participantData,
        agents: agentsData?.agents,
        lang: i18n.language,
        currentValue: formData.creator,
      }),
    [agentsData?.agents, formData.creator, i18n.language, participantData],
  );

  useEffect(() => {
    const agentIri = agentsData?.agents?.[0]?.id;
    const participantIri = participantData?.id || "";

    setFormData((prev) => {
      const nextPublisher = prev.publisher || participantIri || "";
      const nextCreator = creatorTouchedRef.current
        ? prev.creator
        : participantIri || agentIri || "";

      if (nextPublisher === prev.publisher && nextCreator === prev.creator) {
        return prev;
      }

      return {
        ...prev,
        publisher: nextPublisher,
        creator: nextCreator,
      };
    });
  }, [agentsData?.agents, participantData?.id]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid || loading) return;

    const payload = {
      access_rights: formData.access_rights || "",
      applicable_legislation: parseList(appLegislationText),
      creator: formData.creator || "",
      description: formData.description,
      distributions: formData.distributions,
      frequency: formData.frequency || "",
      identifier: formData.identifier || "",
      keywords: Array.isArray(formData.keywords) ? formData.keywords : [],
      landing_page: formData.landing_page || "",
      language: Array.isArray(formData.language)
        ? formData.language[0] || ""
        : formData.language || "",
      modified: formData.modified || "",
      publisher: formData.publisher || "",
      spatial: Array.isArray(formData.spatial) ? formData.spatial : [],
      theme: Array.isArray(formData.theme) ? formData.theme : [],
      title: formData.title,
      version_info: formData.version_info || "",
      version_notes: formData.version_notes,
    };

    createOffering(payload);
  };

  const freqLabels = controlledVocabularies.frequencies.map(
    (item) => item.label,
  );
  const freqValues = controlledVocabularies.frequencies.map(
    (item) => item.concept_iri,
  );

  const langLabels = controlledVocabularies.languages.map((item) => item.label);

  const langValues = controlledVocabularies.languages.map(
    (item) => item.concept_iri,
  );

  const themeLabels = controlledVocabularies.themes.map((item) => item.label);
  const themeValues = controlledVocabularies.themes.map(
    (item) => item.concept_iri,
  );

  const rightLabels = controlledVocabularies.rights.map((item) => item.label);
  const rightValues = controlledVocabularies.rights.map(
    (item) => item.concept_iri,
  );

  const countriesLabels = controlledVocabularies.countries.map(
    (item) => item.label,
  );
  const countriesValues = controlledVocabularies.countries.map(
    (item) => item.concept_iri,
  );

  const creatorLabels = useMemo(
    () => creatorOptions.map((item) => item.label),
    [creatorOptions],
  );

  const creatorValues = useMemo(
    () => creatorOptions.map((item) => item.value),
    [creatorOptions],
  );

  return (
    <section className="grisback pb4">
      <div className="whiteback W100">
        <div>
          <BreadCrumb />
        </div>
      </div>

      <div className="containerflex">
        <div className="whiteback W100 padding2rem">
          <div className="w90">
            <h2 className="tittlecatalog">
              {t("wizard.steps.dataset.createDataset")}
            </h2>

            <MultiLang
              t={t}
              baseLang={baseLang}
              languageTabs={languageTabs}
              languageOptions={languageOptions}
              activeLang={activeLang}
              addLanguageTab={handleAddLanguageTab}
              removeLanguageTab={handleRemoveLang}
              setActiveLang={setActiveLang}
              getLangLabel={getLangLabel}
            />
          </div>
        </div>

        <div className="W100 containerflex ptb2">
          <div className="w95 bgwhite">
            {/* Información General */}
            <div
              className="createdivgeneral W100"
              onClick={() =>
                toggleSection(setIsGeneralOpen, ".createdivgeneral")
              }
              style={{ cursor: "pointer" }}
            >
              <h4 className="ptb3lr4">
                {t("wizard.steps.dataset.generalInfo")}
              </h4>
              <ArrowIcon open={isGeneralOpen} />
            </div>

            {isGeneralOpen && (
              <div className="ds-section-body">
                <div className="ds-grid-general">
                  {/* Nombre del conjunto de datos */}
                  <div className="ds-field ds-title">
                    <p className="ptitleinput">
                      {t("wizard.steps.dataset.name")}
                      <span className="required">*</span>
                    </p>
                    {renderTranslatableInput(
                      "title",
                      "Escribe el nombre del conjunto de datos",
                      false,
                    )}
                  </div>

                  {/* Identificador */}
                  <div className="ds-field ds-identifier">
                    <p className="ptitleinput">
                      {t("wizard.steps.dataset.identifier")}
                      <span className="required">*</span>
                    </p>
                    <input
                      className={inputClass}
                      type="text"
                      name="identifier"
                      value={formData.identifier}
                      onChange={handleSimpleChange}
                      placeholder={t(
                        "wizard.steps.dataset.identifier_placeholder",
                      )}
                      disabled={isTranslationMode}
                    />
                  </div>

                  {/* Descripción */}
                  <div className="ds-field ds-description">
                    <p className="ptitleinput">
                      {t("wizard.steps.dataset.description")}
                      <span className="required">*</span>
                    </p>
                    {renderTranslatableInput(
                      "description",
                      t("wizard.steps.dataset.description_placeholder"),
                      true,
                    )}
                  </div>

                  {/* Tema */}
                  <div className="ds-field ds-themes">
                    <p className="ptitleinput">
                      {t("wizard.steps.dataset.theme")}
                      <span className="required">*</span>
                    </p>
                    <DropDownTheme
                      value={formData.theme}
                      onChange={
                        isTranslationMode
                          ? () => {}
                          : (selected) =>
                              setFormData((prev) => ({
                                ...prev,
                                theme: Array.isArray(selected)
                                  ? selected
                                  : selected
                                    ? [selected]
                                    : [],
                              }))
                      }
                      options={themeLabels}
                      optionsValue={themeValues}
                      multiple={false}
                      disabled={isTranslationMode}
                    />
                  </div>

                  {/* Fecha modificación */}
                  <div className="ds-field ds-modified">
                    <p className="ptitleinput">
                      {t("wizard.steps.dataset.modify")}
                      <span className="required">*</span>
                    </p>
                    <input
                      className={inputClass}
                      type="date"
                      name="modified"
                      value={formData.modified}
                      onChange={handleSimpleChange}
                      placeholder={t("wizard.steps.dataset.modify_placeholder")}
                      disabled={isTranslationMode}
                    />
                  </div>
                  <div className="ds-field ds-department">
                    <p className="ptitleinput">
                      {t("wizard.steps.dataset.rights")}
                      <span className="required">*</span>
                    </p>
                    <DropDownRights
                      value={formData.access_rights}
                      onChange={(nextValue) =>
                        setFormData((prev) => ({
                          ...prev,
                          access_rights: nextValue ?? "",
                        }))
                      }
                      options={rightLabels}
                      optionsValue={rightValues}
                      disabled={isTranslationMode}
                    />
                  </div>

                  <div className="ds-field ds-license">
                    <p className="ptitleinput">
                      {t("wizard.steps.dataset.spatial")}
                    </p>
                    <DropDownCountry
                      value={formData.spatial}
                      onChange={(nextValue) =>
                        setFormData((prev) => ({
                          ...prev,
                          spatial: nextValue ?? "",
                        }))
                      }
                      placeholder={t("dropdown.coverage")}
                      options={countriesLabels}
                      optionsValue={countriesValues}
                      disabled={isTranslationMode}
                    />
                  </div>

                  <div className="ds-field ds-creator">
                    <p className="ptitleinput">
                      {t("wizard.steps.dataset.author")}
                    </p>
                    <DropDownCreator
                      value={formData.creator || ""}
                      onChange={(nextValue) => {
                        if (isTranslationMode) return;
                        creatorTouchedRef.current = true;
                        setFormData((prev) => ({
                          ...prev,
                          creator: nextValue ?? "",
                        }));
                      }}
                      options={creatorLabels}
                      optionsValue={creatorValues}
                      disabled={isTranslationMode}
                    />
                  </div>

                  {/* Palabras clave */}
                  <div className="ds-field ds-keywords">
                    <KeywordsInput
                      label={t("wizard.steps.dataset.keywords")}
                      value={formData.keywords}
                      onChange={(next) => {
                        if (isTranslationMode) return;
                        setFormData((prev) => ({ ...prev, keywords: next }));
                      }}
                      placeholder={t(
                        "wizard.steps.dataset.keywords_placeholder",
                      )}
                      disabled={isTranslationMode}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Información adicional */}
            <div
              className="createdivdetails W100"
              onClick={() =>
                toggleSection(setIsAdditionalOpen, ".createdivdetails")
              }
              style={{ cursor: "pointer" }}
            >
              <h4 className="ptb3lr4">
                {t("wizard.steps.dataset.additionalInfo")}
              </h4>
              <ArrowIcon open={isAdditionalOpen} />
            </div>

            {isAdditionalOpen && (
              <div className="ds-section-body">
                {/* Top row (4 fields) */}
                <div className="ds-grid-additional-top">
                  <div className="ds-field">
                    <p className="ptitleinput">
                      {t("wizard.steps.dataset.language")}{" "}
                      <span className="required">*</span>
                    </p>
                    <DropDownLanguage
                      value={formData.language}
                      onChange={
                        isTranslationMode
                          ? () => {}
                          : (newValue) =>
                              setFormData((prev) => ({
                                ...prev,
                                language: newValue ?? "",
                              }))
                      }
                      options={langLabels}
                      optionsValue={langValues}
                      multiple={false}
                      disabled={isTranslationMode}
                    />
                  </div>

                  <div className="ds-field">
                    <p className="ptitleinput">
                      {t("wizard.steps.dataset.version")}
                    </p>
                    <input
                      className={`${inputClass}`}
                      type="text"
                      name="version_info"
                      value={formData.version_info}
                      onChange={handleSimpleChange}
                      placeholder={t(
                        "wizard.steps.dataset.version_placeholder",
                      )}
                      disabled={isTranslationMode}
                    />
                    {versionError && (
                      <p className="error_text">{versionError}</p>
                    )}
                  </div>

                  <div className="ds-field">
                    <p className="ptitleinput">
                      {t("wizard.steps.dataset.update")}
                    </p>
                    <DropDownFrecuency
                      value={formData.frequency}
                      onChange={
                        isTranslationMode
                          ? () => {}
                          : (newValue) =>
                              setFormData((prev) => ({
                                ...prev,
                                frequency: newValue ?? "",
                              }))
                      }
                      name="frequency"
                      options={freqLabels}
                      optionsValue={freqValues}
                      disabled={isTranslationMode}
                    />
                  </div>

                  <div className="ds-field">
                    <p className="ptitleinput">
                      {t("wizard.steps.dataset.url")}
                    </p>
                    <input
                      className={inputClass}
                      type="text"
                      name="landing_page"
                      value={formData.landing_page}
                      onChange={handleSimpleChange}
                      placeholder={t("wizard.steps.dataset.url_placeholder")}
                      disabled={isTranslationMode}
                    />
                    {landingPageError && (
                      <p className="error_text">{landingPageError}</p>
                    )}
                  </div>
                </div>

                {/* Bottom row (2 textareas) */}
                <div className="ds-grid-additional-bottom">
                  <div className="ds-field">
                    <p className="ptitleinput">
                      {t("wizard.steps.dataset.version_notes")}
                      <span className="required">*</span>
                    </p>
                    {renderTranslatableInput(
                      "version_notes",
                      t("wizard.steps.dataset.version_notes_placeholder"),
                      true,
                    )}
                  </div>

                  <div className="ds-field">
                    <p className="ptitleinput">
                      {t("wizard.steps.dataset.legislation")}
                    </p>
                    <textarea
                      rows="8"
                      className={`${inputClass} legislationDataset fontNoto`}
                      value={appLegislationText}
                      onChange={(e) => {
                        if (isTranslationMode) return;
                        const nextValue = e.target.value;
                        setAppLegislationText(nextValue);
                        setLegislationError(validateLegislationText(nextValue));
                      }}
                      placeholder={t(
                        "wizard.steps.dataset.legislation_placeholder",
                      )}
                      disabled={isTranslationMode}
                    />
                    {legislationError && (
                      <p className="error_text">{legislationError}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* Distributions */}
            <div
              className="createdivservices W100"
              onClick={() =>
                toggleSection(setIsDistributionOpen, ".createdivservices")
              }
              style={{ cursor: "pointer" }}
            >
              <h4 className="ptb3lr4">
                {t("wizard.steps.dataset.distribution.section")}
                <span className="required">*</span>
              </h4>
              <ArrowIcon open={isDistributionOpen} />
            </div>
            {isDistributionOpen && (
              <div className="ds-section-body">
                <div className="ds-table-wrap">
                  <Distribution
                    selectionMode="multiple"
                    selectedIds={formData.distributions}
                    onSelectionChange={handleDistributionSelectionChange}
                    selectedPolicies={selectedPolicies}
                    selectedPolicyIris={selectedPolicyIris}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="whiteback W100 p2">
        <div className="w90 flexdivbotones">
          <button className="boton botoncancel" onClick={() => navigate(-1)}>
            {t("dataOfferings.discover.cancel")}
          </button>

          <button
            className={`boton botoncapply ${
              !isFormValid ? "disabled-button" : ""
            }`}
            disabled={!isFormValid}
            onClick={handleSubmit}
          >
            {t("createDataOfferingsDetail.create")}
          </button>
        </div>
      </div>

      {loading && (
        <div className="loading">
          <Spinner size="large" color="var(--main-color)" />
        </div>
      )}
    </section>
  );
}

export default CreateOffering;
